/**
 * IconButton - Backward Compatibility Wrapper
 *
 * Wraps shadcn Button with icon variant + optional Tooltip
 * Uses shadcn Tooltip for hints (replaces legacy PopoutHint)
 */

import * as React from "react"
import { Button } from "./button"
import type { ButtonProps } from "./button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"

export interface IconButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  icon: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  variant?: 'ghost' | 'subtle' | 'solid'
  tooltip?: string
  hint?: string
  hintPosition?: 'top' | 'bottom' | 'left' | 'right'
}

// Map legacy sizes to shadcn sizes
const sizeMap = {
  sm: 'sm' as const,
  md: 'icon' as const,
  lg: 'icon' as const,
}

// Map legacy variants to shadcn variants
const variantMap = {
  ghost: 'ghost' as const,
  subtle: 'secondary' as const,
  solid: 'default' as const,
}

// Map legacy hint positions to shadcn tooltip sides
const sideMap = {
  top: 'top' as const,
  bottom: 'bottom' as const,
  left: 'left' as const,
  right: 'right' as const,
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', variant = 'ghost', tooltip, hint, hintPosition = 'bottom', className, ...props }, ref) => {
    const button = (
      <Button
        ref={ref}
        variant={variantMap[variant]}
        size={sizeMap[size]}
        title={tooltip}
        className={className}
        {...props}
      >
        {icon}
      </Button>
    )

    // If hint is provided, wrap in Tooltip
    if (hint) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {button}
            </TooltipTrigger>
            <TooltipContent side={sideMap[hintPosition]}>
              <p>{hint}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return button
  }
)

IconButton.displayName = 'IconButton'
